import { ExcelAsset } from '@Types/Asset.types';
import { FilterOption } from '@Types/Form.types.ts';
import { PaginatedResponse } from '@Types/Response.types';

const { VITE_APP_API_URL } = import.meta.env;

export const API_URL = `${VITE_APP_API_URL}`;

export const FIRST_PAGE_NUMBER = 1;

export const PAGE_PROP = 'page';

export const PATHS = {
  USERS: '/users',
  ASSETS: '/assets',
  ACCESSORIES: '/accessories',
  ASSET_DETAILS: '/assets/:id',
  ACCESSORY_DETAILS: '/accessories/:id',
  ASSET_STATUS: '/settings/asset-status',
  ASSET_TEMPLATES: '/settings/asset-template',
  ASSET_TYPES: '/settings/asset-type',
  EMAIL: '/settings/email',
  ACCESSORY_SETTINGS: '/settings/accessories',
  HOME: '/',
  LOCATIONS: '/settings/locations',
  NOTFOUND: '*',
  REPORTS: '/reports',
  REPORT_DETAILS: '/reports/:id',
  SETTINGS: '/settings',
  TAX_STATUS: '/settings/tax-status',
  SCANNER_LOG: '/reports/scanner-log',
  UNAUTHORIZED: '/unauthorized',
  ADMIN_LOG: '/users/admin-log',
  NOTIFICATIONS: '/notifications',
  USER_DETAILS: '/users/:id',
};

export const API_ROUTES = {
  ASSET: {
    GET: `${API_URL}/assets`,
    POST: `${API_URL}/assets/create`,
    SEARCH_POST: `${API_URL}/assets/search`,
    EDIT: `${API_URL}/assets/edit`,
    PUT: `${API_URL}/assets/assign`,
    UNASSIGN: `${API_URL}/assets/unassign`,
    UPLOAD: `${API_URL}/assets/upload`,
  },
  ALERT: {
    UPDATE: `${API_URL}/alerts/update-all`,
    GET: `${API_URL}/alerts`,
    HIDE: `${API_URL}/alerts/hide`,
    VIEW: `${API_URL}/alerts/view`,
  },
  ASSET_TEMPLATE: {
    GET: `${API_URL}/asset-template`,
    POST: `${API_URL}/asset-template/create`,
    DISABLE: `${API_URL}/asset-template/disable`,
    EDIT: `${API_URL}/asset-template/edit`,
    SEARCH_POST: `${API_URL}/asset-template/search`,
  },
  ASSET_TYPE: {
    GET: `${API_URL}/asset-types`,
    POST: `${API_URL}/asset-types/create`,
    DISABLE: `${API_URL}/asset-types/disable`,
    PUT: `${API_URL}/asset-types/update`,
    SEARCH_POST: `${API_URL}/asset-types/search`,
  },
  ASSET_STATUS: {
    GET: `${API_URL}/asset-status`,
    POST: `${API_URL}/asset-status/create`,
    DISABLE: `${API_URL}/asset-status/disable`,
    PUT: `${API_URL}/asset-status/update`,
    SEARCH_POST: `${API_URL}/asset-status/search`,
  },
  AUTH: {
    VALIDATE_USER: `${API_URL}/auth`,
    USER_BY_TOKEN: `${API_URL}/users/info`,
  },
  LOCATION: {
    GET: `${API_URL}/locations`,
    POST: `${API_URL}/locations/create`,
    DISABLE: `${API_URL}/locations/disable`,
    PUT: `${API_URL}/locations/update`,
    SEARCH_POST: `${API_URL}/locations/search`,
  },
  EMAIL: {
    GET: `${API_URL}/email`,
    PUT: `${API_URL}/email/edit`,
    WELCOME: `${API_URL}/email/welcome`,
    SEARCH_POST: `${API_URL}/email/search`,
  },
  USERS: {
    GET_ADMIN: `${API_URL}/users`,
    CREATE_ADMIN: `${API_URL}/users/create`,
    GET_ADMIN_TYPES: `${API_URL}/users/types`,
    DISABLE_ADMIN: `${API_URL}/users/disable`,
    SEARCH_POST: `${API_URL}/users/search`,
    USERS_LIST: 'https://admin.googleapis.com/$discovery/rest?version=directory_v1',
    EDIT_ADMIN: `${API_URL}/users/edit`,
    GET_ASSETS: `${API_URL}/users/assets`,
  },
  DASHBOARD: {
    WEEKLY_COMPARISONS: `${API_URL}/asset-log/weekly-comparisons`,
    READER_SERVICE_STATUS: `${API_URL}/reader-service/status`,
    ASSETS_BY_ASSET_STATUS: `${API_URL}/assets/assets-by-status`,
    READER_SERVICE_STATUS_COUNT: `${API_URL}/reader-service-log/weekly-count`,
  },
  TAX_STATUS: {
    GET: `${API_URL}/tax-status`,
    POST: `${API_URL}/tax-status/create`,
    DISABLE: `${API_URL}/tax-status/disable`,
    PUT: `${API_URL}/tax-status/update`,
    SEARCH_POST: `${API_URL}/tax-status/search`,
  },
  REPORT: {
    GET: `${API_URL}/reports`,
    POST: `${API_URL}/reports/create`,
    SEARCH_POST: `${API_URL}/reports/search`,
    EXCEL: `${API_URL}/reports/excel`,
    PAGE_DATA: `${API_URL}/reports/data`,
  },
  ASSET_LOG: {
    GET: `${API_URL}/asset-log`,
    SEARCH_POST: `${API_URL}/asset-log/search`,
  },
  USER_LOG: {
    GET: `${API_URL}/user-log`,
  },
  ACCESSORIES: {
    GET: `${API_URL}/accessories`,
    POST: `${API_URL}/accessories/create`,
    DISABLE: `${API_URL}/accessories/disable`,
    PUT: `${API_URL}/accessories/update`,
    SEARCH_POST: `${API_URL}/accessories/search`,
    ASSIGN: `${API_URL}/accessories/assign`,
    UNASSIGN: `${API_URL}/accessories/unassign`,
  },
};

export const VALID_EMAIL_REGEX = [
  /^[a-zA-Z1-9]+@konrad.com$/,
  /^[a-zA-Z1-9]+@konradgroup.com$/,
  /^[a-zA-Z0-9]+@([a-zA-Z]+\.)?brainstation\.io$/,
];

export const ALERT_SEVERITIES = {
  SUCCESS: 'success',
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
};

export type AlertProps = {
  type: string;
  severity: string;
};

export type AlertTypeProps = 'WARRANTY' | 'OBSOLETE' | 'CHECK_IN';

export const ALERT_TYPES = {
  WARRANTY: { type: 'ExpiresSoon', severity: ALERT_SEVERITIES.ERROR },
  OBSOLETE: { type: 'Obsolete', severity: ALERT_SEVERITIES.INFO },
  CHECK_IN: { type: 'CheckIn', severity: ALERT_SEVERITIES.WARNING },
};

export const SETTINGS_CARDS = [
  {
    name: 'Locations',
    image: '/images/locations-settings-card.jpg',
    path: PATHS.LOCATIONS,
  },
  {
    name: 'Tax Status',
    image: '/images/tax-status-settings-card.jpg',
    path: PATHS.TAX_STATUS,
  },
  {
    name: 'Asset Types',
    image: '/images/asset-types-settings-card.jpg',
    path: PATHS.ASSET_TYPES,
  },
  {
    name: 'Asset Status',
    image: '/images/asset-status-settings-card.jpg',
    path: PATHS.ASSET_STATUS,
  },
  {
    name: 'Email',
    image: '/images/email-settings-card.jpg',
    path: PATHS.EMAIL,
  },
  {
    name: 'Accessories',
    image: '/images/accessories-settings-card.jpeg',
    path: PATHS.ACCESSORY_SETTINGS,
  },
  {
    name: 'Asset Templates',
    image: '/images/templates-settings-card.png',
    path: PATHS.ASSET_TEMPLATES,
  },
];

export const HTTP_METHODS = {
  GET: 'get',
  POST: 'post',
  PUT: 'put',
};

export const ASSET_FORM_DATA_KEYS = {
  FIELDS: 'fields',
  DOCUMENTS: 'documents',
};

export const ASSET_CONTROL_PERMISSIONS = {
  ACCESS: 'app.assetcontrol',
  ADMIN: 'assetcontrol.admin',
  LEAD: 'assetcontrol.lead',
} as const;

export const ADMIN_ROLES = [ASSET_CONTROL_PERMISSIONS.ADMIN];

export const REGULAR_ROLES = [
  ASSET_CONTROL_PERMISSIONS.ACCESS,
  ASSET_CONTROL_PERMISSIONS.ADMIN,
  ASSET_CONTROL_PERMISSIONS.LEAD,
];

export const FILE_VALIDATION = {
  MAX_SIZE: 4194304, // 4 MB
  ALLOWED_EXTENSIONS: '.jpg, .jpeg, .png, .pdf',
};

export const FILE_NAME_FORMAT = '_';

export const FILENAME_REGEX = /^(.*?)\//;

export const REPORT_DATA_RADIO_BUTTON = [
  {
    VALUE: 'ASSET',
    LABEL: 'Assets',
  },
  {
    VALUE: 'ASSET_LOG',
    LABEL: 'Asset Logs',
  },
];

export const SEARCH_PROP = 'search';
export const TAB_PROP = 'tab';
export const PERSISTENT_FILTER_PROP = 'persistentFilters';

export const TAB_VALUES = {
  ASSETS: 0,
  ACCESSORIES: 1,
  USERS: 2,
  ADMINS: 3,
};

export const REPORT_TYPE = {
  ASSET: 'ASSET',
  LOG: 'ASSET_LOG',
  OFFBOARDING: 'OFFBOARDING',
};

export const PAGE_SIZE = 15;

export const EXCEL_ASSET_FIELD_NAMES: Record<string, string> = {
  assetMake: 'Device Brand',
  assetModel: 'Model',
  assetYear: 'Year',
  assetType: 'Asset Type',
  assetStatus: 'Asset Status',
  osVersion: 'Chip',
  descriptionDetails: 'Description',
  sharedDevice: 'Shared QA Device',
  officeName: 'Office',
  locationDetail: 'Office Details',
  inOffice: 'In Location',
  serialNumber: 'Serial Number',
  assetTag: 'Asset Number',
  rfid: 'RFID',
  taxStatus: 'Tax Status',
  vendor: 'Vendor',
  purchaseDate: 'Date of Purchase',
  warrantyExpiry: 'Warranty Expiry',
  invoiceNumber: 'Invoice Number',
  insideRegime: 'Inside Regime',
  owner: 'Owner',
  storage: 'Storage',
};

export const EXCEL_FILE_LOCATION = 'docs/Asset_Control_Template.xlsx';

export const PAGINATED_DEFAULT: PaginatedResponse<never> = {
  docs: [],
  page: 1,
  hasNextPage: false,
  limit: 1,
  hasPrevPage: false,
  nextPage: undefined,
  pagingCounter: 0,
  prevPage: undefined,
  totalDocs: 0,
  totalPages: 0,
};

export enum FIELD_NAMES {
  ASSET_MAKE = 'assetMake',
  ASSET_MODEL = 'assetModel',
  ASSET_YEAR = 'assetYear',
  ASSIGNED_EMPLOYEE = 'assignedEmployee',
  DESCRIPTION_DETAILS = 'descriptionDetails',
  ASSET_TYPE = 'assetType',
  ASSET_STATUS = 'assetStatus',
  CREATED_AT = 'createdAt',
  OS_VERSION = 'osVersion',
  STORAGE = 'storage',
  SHARED_DEVICE = 'sharedDevice',
  OFFICE_ID = 'officeId',
  OFFICE_NAME = 'officeName',
  OFFICE_COUNTRY = 'officeCountry',
  OFFICE_LOCATION = 'officeLocation',
  LOCATION_DETAIL = 'locationDetail',
  LOGGED_AT = 'loggedAt',
  IN_OFFICE = 'inOffice',
  SERIAL_NUMBER = 'serialNumber',
  ASSET_TAG = 'assetTag',
  RFID = 'rfid',
  TAX_STATUS = 'taxStatus',
  VENDOR = 'vendor',
  PURCHASE_DATE = 'purchaseDate',
  WARRANTY_EXPIRY = 'warrantyExpiry',
  DOCUMENTS = 'documents',
  INVOICE_NUMBER = 'invoiceNumber',
  INSIDE_REGIME = 'insideRegime',
  OWNER = 'owner',
  REPORT_TYPE_FIELD = 'reportType',
}

export const EXCEL_VALUE_MAP = new Map<number, keyof ExcelAsset>([
  [0, FIELD_NAMES.ASSET_MAKE],
  [1, FIELD_NAMES.ASSET_MODEL],
  [2, FIELD_NAMES.ASSET_YEAR],
  [3, FIELD_NAMES.ASSET_TYPE],
  [4, FIELD_NAMES.ASSET_STATUS],
  [5, FIELD_NAMES.OS_VERSION],
  [6, FIELD_NAMES.DESCRIPTION_DETAILS],
  [7, FIELD_NAMES.SHARED_DEVICE],
  [8, FIELD_NAMES.OFFICE_NAME],
  [9, FIELD_NAMES.LOCATION_DETAIL],
  [10, FIELD_NAMES.IN_OFFICE],
  [11, FIELD_NAMES.SERIAL_NUMBER],
  [12, FIELD_NAMES.ASSET_TAG],
  [13, FIELD_NAMES.RFID],
  [14, FIELD_NAMES.TAX_STATUS],
  [15, FIELD_NAMES.VENDOR],
  [16, FIELD_NAMES.PURCHASE_DATE],
  [17, FIELD_NAMES.WARRANTY_EXPIRY],
  [18, FIELD_NAMES.INVOICE_NUMBER],
  [19, FIELD_NAMES.INSIDE_REGIME],
  [20, FIELD_NAMES.OWNER],
  [21, FIELD_NAMES.STORAGE],
]);

export const NON_STRING_FIELDS = [
  FIELD_NAMES.SHARED_DEVICE,
  FIELD_NAMES.IN_OFFICE,
  FIELD_NAMES.PURCHASE_DATE,
  FIELD_NAMES.WARRANTY_EXPIRY,
  FIELD_NAMES.INSIDE_REGIME,
];

export const NAV_VALUES = {
  HOME: {
    id: 'nav-home',
    title: 'Home',
    href: PATHS.HOME,
    roles: ASSET_CONTROL_PERMISSIONS.ADMIN,
  },
  ASSETS: {
    id: 'nav-assets',
    title: 'Assets',
    href: `${PATHS.ASSETS}?${TAB_PROP}=${TAB_VALUES.ASSETS}&${PAGE_PROP}=${FIRST_PAGE_NUMBER}`,
    roles: ASSET_CONTROL_PERMISSIONS.ACCESS,
  },
  REPORTS: {
    id: 'nav-reports',
    title: 'Reports',
    href: `${PATHS.REPORTS}?${PAGE_PROP}=${FIRST_PAGE_NUMBER}`,
    roles: ASSET_CONTROL_PERMISSIONS.ADMIN,
  },
  USERS: {
    id: 'nav-users',
    title: 'Users',
    href: `${PATHS.USERS}?${TAB_PROP}=${TAB_VALUES.USERS}&${PAGE_PROP}=${FIRST_PAGE_NUMBER}`,
    roles: ASSET_CONTROL_PERMISSIONS.ADMIN,
  },
  SETTINGS: {
    id: 'nav-settings',
    title: 'Settings',
    href: PATHS.SETTINGS,
    roles: ASSET_CONTROL_PERMISSIONS.ADMIN,
  },
};

export const ASSET_TAB_VALUES = [
  {
    id: 'tab-assets',
    title: 'Assets',
    href: `${PATHS.ASSETS}?${TAB_PROP}=${TAB_VALUES.ASSETS}&${PAGE_PROP}=${FIRST_PAGE_NUMBER}`,
  },
  {
    id: 'tab-accessories',
    title: 'Accessories',
    href: `${PATHS.ASSETS}?${TAB_PROP}=${TAB_VALUES.ACCESSORIES}&${PAGE_PROP}=${FIRST_PAGE_NUMBER}`,
  },
];

export const USER_TAB_VALUES = [
  {
    id: 'tab-users',
    title: 'Users',
    href: `${PATHS.USERS}?${TAB_PROP}=${TAB_VALUES.USERS}&${PAGE_PROP}=${FIRST_PAGE_NUMBER}`,
  },
  {
    id: 'tab-admins',
    title: 'Admins',
    href: `${PATHS.USERS}?${TAB_PROP}=${TAB_VALUES.ADMINS}&${PAGE_PROP}=${FIRST_PAGE_NUMBER}`,
  },
];

export const TABLE_VALUES = {
  ACCESSORY: {
    NAME: {
      key: 'name',
      header: 'Name',
    },
  },
  ADMIN: {
    PICTURE: {
      key: 'photoUrl',
      header: 'Photo',
      picture: true,
      altKey: 'name',
    },
    EMAIL: {
      key: 'email',
      header: 'Email',
    },
    NAME: {
      key: 'name',
      header: 'Name',
    },
    ROLE: {
      key: 'userType',
      header: 'Role',
    },
  },
  ADMIN_LOG: {
    CHANGE_DETAILS: {
      key: 'details',
      header: 'Change Details',
    },
    LOGGED_AT: {
      key: 'loggedAt',
      header: 'Logged At',
    },
    MADE_BY: {
      key: 'madeBy',
      header: 'Made By',
    },
    TYPE_OF_CHANGE: {
      key: 'changeType',
      header: 'Type of Change',
    },
  },
  ASSET: {
    ASSET_DETAILS: {
      key: 'descriptionDetails',
      header: 'Asset Details',
    },
    ASSET_MAKE: {
      key: 'assetMake',
      header: 'Device Brand',
    },
    ASSET_MODEL: {
      key: 'assetModel',
      header: 'Asset Model',
    },
    ASSET_STATUS: {
      key: 'assetStatus',
      header: 'Asset Status',
    },
    ASSET_TAG: {
      key: 'assetTag',
      header: 'Asset Tag',
    },
    ASSET_TYPE: {
      key: 'assetType',
      header: 'Asset Type',
    },
    ASSET_TYPE_CODE: {
      key: 'assetTypeCode',
      header: 'Asset Type Code',
    },
    ASSET_YEAR: {
      key: 'assetYear',
      header: 'Asset Year',
    },
    CREATED_AT: {
      key: 'createdAt',
      header: 'Created At',
    },
    IN_OFFICE: {
      key: 'inOffice',
      header: 'In Office',
      boolean: true,
    },
    INSIDE_REGIME: {
      key: 'insideRegime',
      header: 'Inside Regime',
      boolean: true,
    },
    INVOICE_NUMBER: {
      key: 'invoiceNumber',
      header: 'Invoice Number',
    },
    LOCATION_DETAIL: {
      key: 'locationDetail',
      header: 'Location Detail',
    },
    OFFICE_COUNTRY: {
      key: 'officeCountry',
      header: 'Office Country',
    },
    OFFICE_LOCATION: {
      key: 'officeLocation',
      header: 'Office Location',
    },
    OFFICE_NAME: {
      key: 'officeName',
      header: 'Office Name',
      default: '-',
    },
    OS_VERSION: {
      key: 'osVersion',
      header: 'Chip',
    },
    STORAGE: {
      key: 'storage',
      header: 'Storage',
    },
    OWNER_EMAIL: {
      key: 'owner',
      header: "Owner's Email",
    },
    PURCHASE_DATE: {
      key: 'purchaseDate',
      header: 'Purchase Date',
    },
    RFID: {
      key: 'rfid',
      header: 'RFID',
    },
    SERIAL_NUMBER: {
      key: 'serialNumber',
      header: 'Serial Number',
    },
    SHARED_DEVICE: {
      key: 'sharedDevice',
      header: 'Shared Device',
      boolean: true,
    },
    TAX_STATUS: {
      key: 'taxStatus',
      header: 'Tax Status',
    },
    TIMEZONE: {
      key: 'timezone',
      header: 'Timezone',
    },
    VENDOR: {
      key: 'vendor',
      header: 'Vendor',
    },
    WARRANTY_EXPIRY: {
      key: 'warrantyExpiry',
      header: 'Warranty Expiry',
    },
  },
  ASSET_TEMPLATE: {
    NAME: {
      key: 'name',
      header: 'Name',
    },
  },
  ASSET_LOG: {
    LOG_TYPE: {
      key: 'logType',
      header: 'Log Type',
    },
    LOGGED_AT: {
      key: 'loggedAt',
      header: 'Logged At',
    },
  },
  ASSET_STATUS: {
    DESCRIPTION: {
      key: 'description',
      header: 'Description',
    },
  },
  ASSET_TYPES: {
    CODE: {
      key: 'assetCode',
      header: 'Code',
    },
    DESCRIPTION: {
      key: 'description',
      header: 'Description',
    },
  },
  CHANGE_HISTORY: {
    DATE: {
      key: 'createdAt',
      header: 'Created At',
    },
    DETAILS: {
      key: 'details',
      header: 'Details',
    },
    TYPE: {
      key: 'changeType',
      header: 'Change Type',
    },
    USER: {
      key: 'madeBy',
      header: 'Made By',
    },
  },
  EMAIL: {
    TEMPLATE: {
      key: 'templateName',
      header: 'Template',
    },
  },
  LOCATIONS: {
    COUNTRY: {
      key: 'country',
      header: 'Office Country',
    },
    DETAILS: {
      key: 'description',
      header: 'Location Detail',
    },
    LOCATION: {
      key: 'location',
      header: 'Office Location',
    },
    NAME: {
      key: 'name',
      header: 'Name',
    },
  },
  NOTIFICATIONS: {
    DATE: {
      key: 'date',
      header: 'Date & Time',
    },
    MESSAGE: {
      key: 'message',
      header: 'Message',
    },
    TYPE: {
      key: 'type',
      header: 'Type',
    },
  },
  OWNERS: {
    DATE: {
      key: 'assignmentDate',
      header: 'Date & Time',
    },
    OWNER: {
      key: 'employeeEmail',
      header: 'Owner',
    },
  },
  REPORTS: {
    ASSET_TAG: {
      key: 'assetTag',
      header: 'Asset Tag',
    },
    ASSET_TYPE: {
      key: 'assetType',
      header: 'Asset Type',
    },
    ASSET_MODEL: {
      key: 'assetModel',
      header: 'Asset Model',
    },
    CREATED_AT: {
      key: 'createdAt',
      header: 'Created At',
    },
    CREATED_BY: {
      key: 'createdBy',
      header: 'Created By',
    },
    NAME: {
      key: 'name',
      header: 'Name',
    },
    OWNER_EMAIL: {
      key: 'owner',
      header: 'Owner',
    },
    SERIAL_NUMBER: {
      key: 'serialNumber',
      header: 'Serial Number',
    },
    TYPE: {
      key: 'reportType',
      header: 'Report Type',
    },
  },
  REPORT_ASSET_LOG: {
    LOGGED_AT: {
      key: 'loggedAt',
      header: 'Logged At',
    },
    LOG_TYPE: {
      key: 'logType',
      header: 'Log Type',
    },
    RFID: {
      key: 'rfid',
      header: 'RFID',
    },
    OFFICE_NAME: {
      key: 'officeName',
      header: 'Office Name',
    },
    OFFICE_COUNTRY: {
      key: 'officeCountry',
      header: 'Office Country',
    },
    OFFICE_LOCATION: {
      key: 'officeLocation',
      header: 'Office Location',
    },
    TIMEZONE: {
      key: 'timezone',
      header: 'Timezone',
    },
  },
  SCANNER_LOG: {
    ASSET_DETAILS: {
      key: 'assetDetails',
      header: 'Asset Details',
    },
    DATE_TIME: {
      key: 'loggedAt',
      header: 'Date & Time',
    },
    EVENT: {
      key: 'logType',
      header: 'Event',
    },
    OFFICE: {
      key: 'officeName',
      header: 'Office',
    },
    OWNER: {
      key: 'owner',
      header: 'Owner',
    },
    RFID: {
      key: 'rfid',
      header: 'RFID',
    },
  },
  TAX_STATUS: {
    DESCRIPTION: {
      key: 'description',
      header: 'Description',
    },
  },
  USER: {
    PICTURE: {
      key: 'photoUrl',
      header: 'Photo',
      picture: true,
      altKey: 'fullName',
    },
    EMAIL: {
      key: 'email',
      header: 'Email',
    },
    NAME: {
      key: 'fullName',
      header: 'Name',
    },
    USER_DETAILS: {
      key: 'descriptionDetails',
      header: 'User Details',
    },
    OFFICE_NAME: {
      key: 'office.Name',
      header: 'Office Name',
      default: '-',
    },
  },
  USER_ASSET_HISTORY: {
    ASSIGNMENT_DATE: {
      key: 'assignmentDate',
      header: 'Date Assigned',
    },
    UNASSIGNMENT_DATE: {
      key: 'unassignmentDate',
      header: 'Date Unassigned',
    },
  },
};

export const ASSET_DETAILS = [
  {
    title: 'Device Information',
    fields: [
      { label: 'Model', key: 'assetModel' },
      { label: 'Device Brand', key: 'assetMake' },
      { label: 'Year', key: 'assetYear' },
      { label: 'Type', key: 'assetType' },
      { label: 'Status', key: 'assetStatus' },
      { label: 'Chip', key: 'osVersion' },
      { label: 'Storage', key: 'storage' },
      { label: 'Description', key: 'descriptionDetails' },
      { label: 'Shared QA Device', key: 'sharedDevice' },
    ],
  },
  {
    title: 'Legal Information',
    fields: [
      { label: 'Serial Number', key: 'serialNumber' },
      { label: 'Asset Number', key: 'assetTag' },
      { label: 'RFID', key: 'rfid' },
      { label: 'Tax Status', key: 'taxStatus' },
      { label: 'Vendor', key: 'vendor' },
      { label: 'Purchase Date', key: 'purchaseDate' },
      { label: 'Warranty Expiry', key: 'warrantyExpiry' },
      { label: 'Invoice Number', key: 'invoiceNumber' },
      { label: 'Inside Regime', key: 'insideRegime' },
    ],
  },
  {
    title: 'Location Information',
    fields: [
      { label: 'Office', key: 'officeName' },
      { label: 'Country', key: 'officeCountry' },
      { label: 'Location', key: 'officeLocation' },
      { label: 'Details', key: 'officeDescription' },
      { label: 'Location Detail', key: 'locationDetail' },
      { label: 'In Location', key: 'inOffice' },
    ],
  },
];

export const SCANNER_CARDS = [
  {
    amount: '',
    description: '',
    icon: 'fa-arrow-right-from-bracket',
    logTypeId: 'Check-Out',
    percentage: '',
    title: 'Check out events',
  },
  {
    amount: '',
    description: '',
    icon: 'fa-arrow-left-from-bracket',
    logTypeId: 'Check-In',
    percentage: '',
    title: 'Check in events',
  },
];

export const TABLE_STRUCTURES = {
  ACCESSORY: [TABLE_VALUES.ACCESSORY.NAME],
  ADMIN: [TABLE_VALUES.ADMIN.PICTURE, TABLE_VALUES.ADMIN.NAME, TABLE_VALUES.ADMIN.EMAIL],
  ADMIN_LOG: [
    TABLE_VALUES.ADMIN_LOG.MADE_BY,
    TABLE_VALUES.ADMIN_LOG.TYPE_OF_CHANGE,
    TABLE_VALUES.ADMIN_LOG.CHANGE_DETAILS,
    TABLE_VALUES.ADMIN_LOG.LOGGED_AT,
  ],
  ASSET: [
    TABLE_VALUES.ASSET.ASSET_TAG,
    TABLE_VALUES.ASSET.SERIAL_NUMBER,
    TABLE_VALUES.ASSET.ASSET_MODEL,
    TABLE_VALUES.ASSET.OFFICE_NAME,
    TABLE_VALUES.ASSET.OWNER_EMAIL,
  ],
  ASSET_STATUS: [TABLE_VALUES.ASSET_STATUS.DESCRIPTION],
  ASSET_TEMPLATES: [TABLE_VALUES.ASSET_TEMPLATE.NAME],
  ASSET_TYPES: [TABLE_VALUES.ASSET_TYPES.CODE, TABLE_VALUES.ASSET_TYPES.DESCRIPTION],
  ASSETS_USER: [TABLE_VALUES.ASSET.ASSET_TAG, TABLE_VALUES.ASSET.SERIAL_NUMBER, TABLE_VALUES.ASSET.ASSET_MODEL],
  CHANGE_HISTORY: [
    TABLE_VALUES.CHANGE_HISTORY.TYPE,
    TABLE_VALUES.CHANGE_HISTORY.USER,
    TABLE_VALUES.CHANGE_HISTORY.DATE,
    TABLE_VALUES.CHANGE_HISTORY.DETAILS,
  ],
  EMAILS: [TABLE_VALUES.EMAIL.TEMPLATE],
  LOCATIONS: [
    TABLE_VALUES.LOCATIONS.NAME,
    TABLE_VALUES.LOCATIONS.LOCATION,
    TABLE_VALUES.LOCATIONS.COUNTRY,
    TABLE_VALUES.LOCATIONS.DETAILS,
  ],
  NOTIFICATIONS: [TABLE_VALUES.NOTIFICATIONS.TYPE, TABLE_VALUES.NOTIFICATIONS.MESSAGE, TABLE_VALUES.NOTIFICATIONS.DATE],
  OWNERS: [TABLE_VALUES.OWNERS.OWNER, TABLE_VALUES.OWNERS.DATE],
  REPORTS: [
    TABLE_VALUES.REPORTS.NAME,
    TABLE_VALUES.REPORTS.TYPE,
    TABLE_VALUES.REPORTS.CREATED_BY,
    TABLE_VALUES.REPORTS.CREATED_AT,
  ],
  REPORTS_ASSET_LOG: [
    TABLE_VALUES.REPORT_ASSET_LOG.LOGGED_AT,
    TABLE_VALUES.REPORT_ASSET_LOG.LOG_TYPE,
    TABLE_VALUES.REPORT_ASSET_LOG.RFID,
    TABLE_VALUES.REPORT_ASSET_LOG.OFFICE_NAME,
  ],
  REPORTS_ASSET: [
    TABLE_VALUES.ASSET.SERIAL_NUMBER,
    TABLE_VALUES.ASSET.ASSET_TAG,
    TABLE_VALUES.ASSET.ASSET_TYPE,
    TABLE_VALUES.ASSET.ASSET_MODEL,
    TABLE_VALUES.ASSET.OWNER_EMAIL,
  ],
  SCANNER_LOG: [
    TABLE_VALUES.SCANNER_LOG.OFFICE,
    TABLE_VALUES.SCANNER_LOG.DATE_TIME,
    TABLE_VALUES.SCANNER_LOG.EVENT,
    TABLE_VALUES.SCANNER_LOG.RFID,
    TABLE_VALUES.SCANNER_LOG.OWNER,
  ],
  TAX_STATUS: [TABLE_VALUES.TAX_STATUS.DESCRIPTION],
  USER: [TABLE_VALUES.USER.PICTURE, TABLE_VALUES.USER.NAME, TABLE_VALUES.USER.EMAIL, TABLE_VALUES.USER.OFFICE_NAME],
  USER_ASSET_HISTORY: [
    TABLE_VALUES.ASSET.SERIAL_NUMBER,
    TABLE_VALUES.ASSET.ASSET_MODEL,
    TABLE_VALUES.USER_ASSET_HISTORY.ASSIGNMENT_DATE,
    TABLE_VALUES.USER_ASSET_HISTORY.UNASSIGNMENT_DATE,
  ],
};

export const SEARCH_TIMER_INTERVAL = 1000;

export const FORM_OPTIONS = {
  REQUIRED: {
    required: {
      value: true,
      message: 'This field is required.',
    },
  },
};

export const DROPDOWN_DEFAULT = { value: 'default', label: 'Select...', isDisabled: true };
export const DROPDOWN_DEFAULT_NUMBER = { value: -1, label: 'Select...', isDisabled: true };

export const enum ELEMENT_TO_DELETE_TYPE {
  ASSET = 'Asset',
  ACCESSORY = 'Accessory',
}

export const OFFBOARDING_PDF_FILE_NAME = 'offboarding.pdf';

export const enum REPORT_FILTER_TYPE {
  CHECKBOX = 'CHECKBOX',
  BOOLEAN_TYPE = 'BOOLEAN',
  DATE = 'DATE',
  KEYWORD = 'KEYWORD',
}

export const REPORT_FILTERS_ASSET: FilterOption[] = [
  {
    name: FIELD_NAMES.ASSET_MAKE,
    label: 'Device Brand',
    placeholder: 'Write the keywords and hit Enter',
    type: REPORT_FILTER_TYPE.KEYWORD,
    valueType: 'text',
  },
  {
    name: FIELD_NAMES.ASSET_MODEL,
    label: 'Asset Model',
    placeholder: 'Write the keywords and hit Enter',
    type: REPORT_FILTER_TYPE.KEYWORD,
    valueType: 'text',
  },
  {
    name: FIELD_NAMES.ASSET_STATUS,
    label: 'Asset Status',
    options: [],
    type: REPORT_FILTER_TYPE.CHECKBOX,
  },
  {
    name: FIELD_NAMES.ASSET_TAG,
    label: 'Asset Tag',
    placeholder: 'Write the keywords and hit Enter',
    type: REPORT_FILTER_TYPE.KEYWORD,
    valueType: 'text',
  },
  {
    name: FIELD_NAMES.ASSET_TYPE,
    label: 'Asset Type',
    options: [],
    type: REPORT_FILTER_TYPE.CHECKBOX,
  },
  {
    name: FIELD_NAMES.ASSET_YEAR,
    label: 'Asset Year',
    placeholder: 'Write the keywords and hit Enter',
    type: REPORT_FILTER_TYPE.KEYWORD,
    valueType: 'number',
  },
  {
    name: FIELD_NAMES.ASSIGNED_EMPLOYEE,
    label: 'Owner',
    placeholder: 'Write the keywords and hit Enter',
    type: REPORT_FILTER_TYPE.KEYWORD,
    valueType: 'text',
  },
  {
    name: FIELD_NAMES.CREATED_AT,
    label: 'Created At',
    type: REPORT_FILTER_TYPE.DATE,
  },
  {
    name: FIELD_NAMES.DESCRIPTION_DETAILS,
    label: 'Description Details',
    placeholder: 'Write the keywords and hit Enter',
    type: REPORT_FILTER_TYPE.KEYWORD,
    valueType: 'text',
  },
  {
    name: FIELD_NAMES.IN_OFFICE,
    label: 'In Office',
    type: REPORT_FILTER_TYPE.BOOLEAN_TYPE,
  },
  {
    name: FIELD_NAMES.INSIDE_REGIME,
    label: 'Inside Regime',
    type: REPORT_FILTER_TYPE.BOOLEAN_TYPE,
  },
  {
    name: FIELD_NAMES.INVOICE_NUMBER,
    label: 'Invoice Number',
    placeholder: 'Write the keywords and hit Enter',
    type: REPORT_FILTER_TYPE.KEYWORD,
    valueType: 'text',
  },
  {
    name: FIELD_NAMES.LOCATION_DETAIL,
    label: 'Location Detail',
    placeholder: 'Write the keywords and hit Enter',
    type: REPORT_FILTER_TYPE.KEYWORD,
    valueType: 'text',
  },
  {
    name: FIELD_NAMES.OFFICE_COUNTRY,
    label: 'Office Country',
    placeholder: 'Write the keywords and hit Enter',
    type: REPORT_FILTER_TYPE.KEYWORD,
    valueType: 'text',
  },
  {
    name: FIELD_NAMES.OFFICE_LOCATION,
    label: 'Office Location',
    options: [],
    type: REPORT_FILTER_TYPE.CHECKBOX,
  },
  {
    name: FIELD_NAMES.OFFICE_NAME,
    label: 'Office Name',
    options: [],
    type: REPORT_FILTER_TYPE.CHECKBOX,
  },
  {
    name: FIELD_NAMES.OS_VERSION,
    label: 'Chip',
    placeholder: 'Write the keywords and hit Enter',
    type: REPORT_FILTER_TYPE.KEYWORD,
    valueType: 'text',
  },
  {
    name: FIELD_NAMES.PURCHASE_DATE,
    label: 'Purchase Date',
    type: REPORT_FILTER_TYPE.DATE,
  },
  {
    name: FIELD_NAMES.RFID,
    label: 'RFID',
    placeholder: 'Write the keywords and hit Enter',
    type: REPORT_FILTER_TYPE.KEYWORD,
    valueType: 'text',
  },
  {
    name: FIELD_NAMES.SERIAL_NUMBER,
    label: 'Serial Number',
    placeholder: 'Write the keywords and hit Enter',
    type: REPORT_FILTER_TYPE.KEYWORD,
    valueType: 'text',
  },
  {
    name: FIELD_NAMES.SHARED_DEVICE,
    label: 'Shared Device',
    type: REPORT_FILTER_TYPE.BOOLEAN_TYPE,
  },
  {
    name: FIELD_NAMES.TAX_STATUS,
    label: 'Tax Status',
    options: [],
    type: REPORT_FILTER_TYPE.CHECKBOX,
  },
  {
    name: FIELD_NAMES.VENDOR,
    label: 'Vendor',
    placeholder: 'Write the keywords and hit Enter',
    type: REPORT_FILTER_TYPE.KEYWORD,
    valueType: 'text',
  },
  {
    name: FIELD_NAMES.WARRANTY_EXPIRY,
    label: 'Warranty Expiry',
    placeholder: 'Write the keywords and hit Enter',
    type: REPORT_FILTER_TYPE.KEYWORD,
    valueType: 'number',
  },
];
export const REPORT_FILTERS_ASSET_LOG = [
  {
    name: FIELD_NAMES.RFID,
    label: 'RFID',
    placeholder: 'Write the keywords and hit Enter',
    type: REPORT_FILTER_TYPE.KEYWORD,
  },
  {
    name: FIELD_NAMES.OFFICE_NAME,
    label: 'Office Name',
    options: [],
    type: REPORT_FILTER_TYPE.CHECKBOX,
  },
  {
    name: FIELD_NAMES.OFFICE_COUNTRY,
    label: 'Office Country',
    placeholder: 'Write the keywords and hit Enter',
    type: REPORT_FILTER_TYPE.KEYWORD,
  },
  {
    name: FIELD_NAMES.OFFICE_LOCATION,
    label: 'Office Location',
    options: [],
    type: REPORT_FILTER_TYPE.CHECKBOX,
  },
  {
    name: FIELD_NAMES.LOGGED_AT,
    label: 'Logged At',
    type: REPORT_FILTER_TYPE.DATE,
  },
];

export const COMPANIES = [
  {
    CompanyID: 1,
    Name: 'Konrad Group',
  },
  {
    CompanyID: 3,
    Name: 'BrainStation',
  },
  {
    CompanyID: 4,
    Name: 'Quantum Coffee',
  },
];

export enum ASSET_MODAL_TYPE {
  ASSET = 'ASSET',
  ASSET_TEMPLATE = 'ASSET_TEMPLATE',
}
export const ASSET_TYPES_WITH_REQUIRED_SERIAL_NUMBER = ['Computer Equipment', 'Computers', 'Devices'];

export const RECIPIENT_DROPDOWN_VALUES = [
  {
    label: 'Administrators',
    value: 'Administrators',
    isDisabled: false,
    tooltip: 'Administrators',
  },
  {
    label: 'Employees (If any)',
    value: 'Employees',
    isDisabled: false,
    tooltip: 'Employees (If any)',
  },
];

export const ASSET_FILTER_PARAMS = {
  ASSET_TYPE: 'assetType',
  ASSET_STATUS: 'assetStatus',
  OFFICE_ID: 'officeId',
};

export const USER_FILTER_PARAMS = {
  COMPANY: 'company',
  OFFICE: 'office',
};
